import { get, post, put } from './http';

export type UserRole = 'super_admin' | 'admin' | 'member';
export type MembershipTier = 'member' | 'vip';
export type AccountStatus = 'active' | 'inactive' | 'banned' | 'deleted';

export interface AuthUserDto {
  id: number;
  username?: string | null;
  email: string;
  name: string;
  daohao: string | null;
  namePublic: boolean;
  notifyNewSolution?: boolean;
  notifyToolUpdate?: boolean;
  notifyCommunity?: boolean;
  role: UserRole;
  roles: string[];
  membershipTier: MembershipTier;
  accountStatus: AccountStatus;
  isEmailVerified: boolean;
  company: string | null;
  position: string | null;
  phone: string | null;
  avatarUrl: string | null;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface AuthPayload {
  user: AuthUserDto;
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  accessExpiresIn: string;
  refreshExpiresIn: string;
}

export interface SendCodePayload {
  email: string;
  purpose: 'register' | 'reset';
}

export interface RegisterPayload {
  email: string;
  password: string;
  name: string;
  code: string;
  inviteCode?: string;
  company?: string;
  position?: string;
  phone: string;
  daohao?: string;
}

export interface InviteRecord {
  inviteeName: string;
  inviteeEmail: string;
  rewarded: boolean;
  inviterRewardPoints: number;
  inviteeRewardPoints: number;
  rewardedAt: string | null;
  createdAt: string;
}

export interface MyInviteInfo {
  inviteCode: string | null;
  records: InviteRecord[];
  totalInvited: number;
  rewarded: number;
  earnedPoints: number;
  inviterRewardPoints?: number;
  inviteeRewardPoints?: number;
}

export interface PointsLedgerItem {
  id: number;
  actionKey: string;
  actionLabel: string;
  pointsDelta: number;
  targetType: string;
  targetTitle: string | null;
  remark: string | null;
  createdAt: string;
}

export interface AuthPointsLevelInfo {
  level: number;
  name: string;
  title: string;
  minPoints: number;
  maxPoints: number | null;
  color: string;
  icon: string;
  nextLevelName?: string;
}

export interface MyPointsInfo {
  total: number;
  ledger: PointsLedgerItem[];
  levels?: AuthPointsLevelInfo[];
  currentLevel?: AuthPointsLevelInfo;
  nextLevel?: AuthPointsLevelInfo | null;
  progressToNext?: number;
}

export interface LoginPayload {
  account: string;
  password: string;
}

export interface UpdateProfilePayload {
  name: string;
  daohao?: string | null;
  namePublic?: boolean;
  notifyNewSolution?: boolean;
  notifyToolUpdate?: boolean;
  notifyCommunity?: boolean;
  company?: string | null;
  position?: string | null;
  phone?: string | null;
  avatarUrl?: string | null;
}

export interface ChangePasswordPayload {
  currentPassword: string;
  newPassword: string;
}

export function sendVerificationCode(payload: SendCodePayload) {
  return post<null>('/auth/send-code', payload);
}

export function registerAccount(payload: RegisterPayload) {
  return post<AuthPayload>('/auth/register', payload);
}

export function loginByPassword(payload: LoginPayload) {
  return post<AuthPayload>('/auth/login', payload);
}

export function checkInviteCode(inviteCode: string) {
  return post<{ valid: boolean }>('/auth/check-invite-code', { inviteCode });
}

export function getMyInvite() {
  return get<MyInviteInfo>('/auth/my-invite');
}

export function getMyPoints() {
  return get<MyPointsInfo>('/auth/my-points');
}

export function getMyProfile() {
  return get<AuthUserDto>('/auth/me');
}

export function updateMyProfile(payload: UpdateProfilePayload) {
  return put<AuthUserDto>('/auth/profile', payload);
}

export function changeMyPassword(payload: ChangePasswordPayload) {
  return post<null>('/auth/change-password', payload);
}

export interface ResetPasswordPayload {
  email: string;
  code: string;
  newPassword: string;
}

export function resetPassword(payload: ResetPasswordPayload) {
  return post<null>('/auth/reset-password', payload);
}

export function verifyAdminPasswordBackend(password: string) {
  return post<{ valid: boolean }>('/auth/verify-password', { password });
}

export function deactivateMyAccount() {
  return post<{ message: string }>('/auth/deactivate');
}
